import ApiClient from 'common/services/http-client';
import { CustomLanding } from 'deposit/services/types';

import END_POINTS from 'common/config/constants/urls';
import { buildPathWithParams } from 'common/helpers/url';
import { toCamelCaseObject, toSnakeCaseObject } from 'common/helpers/utils';
import { Country, CountryResponse } from 'common/types/global';

import { AstroCustomHeaders } from '../helpers/cookiesHandler';
import {
  FaceLoginRequestAdapter,
  FaceLoginResponseAdapter,
} from '../modules/authentication/authentication.adapter';
import {
  FaceLoginAPiResponse,
  FaceLoginRequest,
} from '../modules/authentication/authentication.type';

export enum LoginSignupOperation {
  SIGNUP = 'SIGNUP',
  LOGIN = 'LOGIN',
}

export interface AstroUserResponse {
  id_user: number;
  id_external: string;
  address: {
    first_line: string;
    second_line: string;
    city: string;
    state: string;
    country: string;
    zip: string;
  };
  country: string;
  email: string;
  first_name: string;
  email_confirmation: string;
  force_password: boolean;
  document_checked: boolean;
  kyc_status: string;
  phone_number: string;
  status: string;
  created_at: string;
  birth_date: string;
  user_country: CountryResponse;
}

export interface AstroUser {
  idUser: number;
  idExternal: string;
  address?: {
    firstLine: string;
    secondLine: string;
    city: string;
    state: string;
    country: string;
    zip: string;
  };
  country: string;
  createdAt: string;
  email: string;
  firstName: string;
  lastName: string;
  emailConfirmation: string;
  forcePassword: boolean;
  documentChecked: boolean;
  kycStatus: string;
  phoneNumber: string;
  status: string;
  birthDate: string;
  userCountry: Country;
  documentType: string;
  documentNumber: string;
}
interface LoginSignupUserRequest {
  username: string;
  analytics?: AnalyticsData;
}
export interface LoginSignUpResponse {
  codeId: string;
  operation: LoginSignupOperation;
  voiceCall: boolean;
  kycStatus?: string;
  userName?: string;
}
export interface AnalyticsData {
  event_properties?: {
    [k: string]: string;
  };
  user_properties?: {
    [k: string]: string;
  };
}
interface ValidateCodeRequest {
  codeId: string;
  code: string;
  referalCode?: string;
  merchantDepositToken?: string;
  captchaResponse?: string;
  analytics?: AnalyticsData;
}
interface DepositLandingData {
  depositExternalId: string;
  landing: 'ot' | CustomLanding.PM;
}

export interface Channel {
  id: number;
  name: string;
  text: string;
  default: boolean;
  image: string;
}
export interface ValidateCodeResponse {
  user: AstroUser;
  operation: LoginSignupOperation;
}

export class AuthServices {
  static getCookiesAuth = async (sessionHeaders?: Record<AstroCustomHeaders, string>) => {
    try {
      const { data, headers } = await ApiClient.get<{
        data: {
          checked: boolean;
        };
        headers?: { [key: string]: string };
      }>(END_POINTS.getCookiesAuth, {}, { headers: sessionHeaders ?? {} }, true);

      return { checked: data.checked, headers };
    } catch (error) {
      return { checked: false };
    }
  };

  static loginSignup = async (data: LoginSignupUserRequest) => {
    try {
      const response = await ApiClient.post<LoginSignUpResponse>(END_POINTS.loginSignup, {
        ...toSnakeCaseObject(data),
        analytics: data.analytics,
      });
      return toCamelCaseObject<LoginSignUpResponse>(response);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  static loginDepositLanding = async (data: DepositLandingData) => {
    try {
      await ApiClient.post(END_POINTS.depositLanding, {
        ...toSnakeCaseObject(data),
      });
    } catch (error) {
      console.error('Error', error);
    }
  };

  static validateCode = async (data: ValidateCodeRequest) => {
    try {
      const response = await ApiClient.post<ValidateCodeResponse>(END_POINTS.codeValidate, {
        ...toSnakeCaseObject(data),
        analytics: data.analytics,
      });
      return toCamelCaseObject<ValidateCodeResponse>(response);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  static resendEmail = async (email: string) => {
    return ApiClient.post(END_POINTS.resendEmail, { email });
  };

  static resendCode = async (username: string, channelId: number) => {
    try {
      const response = await ApiClient.post<LoginSignUpResponse>(
        END_POINTS.resendCode,
        toSnakeCaseObject({
          username,
          channelId,
        }),
      );
      return toCamelCaseObject<LoginSignUpResponse>(response);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  static getProfile = async () => {
    try {
      const response = await ApiClient.get<AstroUser>(END_POINTS.userDetails);
      return toCamelCaseObject<AstroUser>(response);
    } catch {
      return Promise.reject();
    }
  };

  static updateProfile = async (updateUser: Partial<AstroUserResponse>) => {
    try {
      const { user } = await ApiClient.patch<{ user: AstroUser }>(
        END_POINTS.userProfile,
        updateUser,
      );
      return toCamelCaseObject<AstroUser>(user);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  static getHasSession = async () => {
    try {
      const user = await ApiClient.get<AstroUser>(END_POINTS.userRefresh);
      return toCamelCaseObject<AstroUser>(user);
    } catch {
      return undefined;
    }
  };

  static getChannels = async (country: string) => {
    try {
      const response = await ApiClient.get<{ channels: Channel[] }>(
        buildPathWithParams(END_POINTS.channels, { country }),
      );
      return response.channels.map((c) => toCamelCaseObject<Channel>(c));
    } catch {
      return [];
    }
  };

  static faceLogin = async (request: FaceLoginRequest['data']) => {
    try {
      const response = await ApiClient.post<FaceLoginAPiResponse>(
        END_POINTS.faceLoginSignup,
        FaceLoginRequestAdapter.adapt(request, 'FACE_VALIDATION', 'INCODE'),
      );
      return FaceLoginResponseAdapter.adapt(response);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  static logout = async () => {
    try {
      return ApiClient.post(END_POINTS.logout);
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
